import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SESSION_KEY = "hostro_chat_session_v1";

const QUICK_ASKS = [
  "Jaipur mein PG chahiye",
  "Food order kaise karu?",
  "Rent payment failed",
  "How to list my PG?",
];

export default function HostroChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Namaste! 👋 Main Hostro Assistant hu. PG, food, laundry, rent — koi bhi sawaal pucho!" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY) || ""; } catch { return ""; }
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (sid) {
      try { localStorage.setItem(SESSION_KEY, sid); } catch { /* */ }
    }
  }, [sid]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async (text) => {
    const t = (text || input).trim();
    if (!t || busy) return;
    setMsgs(m => [...m, { role: "user", text: t }]);
    setInput("");
    setBusy(true);
    try {
      const role = (user && typeof user === "object") ? user.role : "guest";
      const { data } = await api.post("/chat", { message: t, session_id: sid || undefined, role });
      if (data.session_id && !sid) setSid(data.session_id);
      setMsgs(m => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMsgs(m => [...m, { role: "assistant", text: "Sorry, abhi reply nahi de paaya. Phir try karo." }]);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); send(); };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`fixed bottom-5 right-5 z-[80] w-14 h-14 rounded-full grid place-items-center shadow-2xl transition-all duration-300 ${
          open
            ? "bg-rose-600 hover:bg-rose-700 rotate-90"
            : "bg-emerald-600 hover:bg-emerald-700 hover:scale-110"
        } text-white`}
        aria-label="Open Hostro Assistant"
        data-testid="chat-launcher"
      >
        {open ? <X size={22}/> : <MessageCircle size={22}/>}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-[79] w-[calc(100vw-40px)] max-w-md h-[70vh] max-h-[600px] bg-white rounded-3xl border border-gray-200 shadow-2xl flex flex-col overflow-hidden" data-testid="chat-widget">
          {/* Header */}
          <div className="bg-gradient-to-br from-emerald-600 to-teal-500 text-white p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 grid place-items-center">
              <Sparkles size={18}/>
            </div>
            <div className="flex-1">
              <div className="font-display font-black text-lg leading-tight">Hostro Assistant</div>
              <div className="text-xs opacity-90 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse"></span>
                Online · Hindi + English
              </div>
            </div>
            <button onClick={()=>setOpen(false)} className="p-2 rounded-full hover:bg-white/10 transition" aria-label="Close"><X size={18}/></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-emerald-50/40 to-white" data-testid="chat-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-emerald-600 text-white rounded-br-sm"
                    : "bg-white border border-gray-200 text-[#0E1F18] rounded-bl-sm shadow-sm"
                }`} data-testid={`chat-msg-${m.role}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay:"0ms"}}/>
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay:"150ms"}}/>
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay:"300ms"}}/>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick asks (only initial state) */}
          {msgs.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="chat-quick-asks">
              {QUICK_ASKS.map(q => (
                <button key={q} onClick={()=>send(q)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition">
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 border-t border-gray-100 flex gap-2 bg-white">
            <input
              value={input}
              onChange={(e)=>setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
              disabled={busy}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="w-10 h-10 grid place-items-center rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white transition"
              data-testid="chat-send"
            >
              <Send size={16}/>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
